import type { AudioFrame } from '../types';

const ANALYZER_CONFIG = {
  fftSize: 2048,
  smoothingTimeConstant: 0.8,
  minDecibels: -90,
  maxDecibels: -10,
};

let audioContext: AudioContext | null = null;
let analyzer: AnalyserNode | null = null;
let stream: MediaStream | null = null;
let frequencyData: Float32Array | null = null;
let timeDomainData: Float32Array | null = null;

export async function initAudio(): Promise<void> {
  stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  audioContext = new AudioContext();

  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }

  const source = audioContext.createMediaStreamSource(stream);

  analyzer = audioContext.createAnalyser();
  analyzer.fftSize = ANALYZER_CONFIG.fftSize;
  analyzer.smoothingTimeConstant = ANALYZER_CONFIG.smoothingTimeConstant;
  analyzer.minDecibels = ANALYZER_CONFIG.minDecibels;
  analyzer.maxDecibels = ANALYZER_CONFIG.maxDecibels;

  source.connect(analyzer);

  frequencyData = new Float32Array(analyzer.frequencyBinCount);
  timeDomainData = new Float32Array(analyzer.fftSize);
}

export function getAudioFrame(): AudioFrame | null {
  if (!analyzer || !frequencyData || !timeDomainData) return null;

  analyzer.getFloatFrequencyData(frequencyData as Float32Array<ArrayBuffer>);
  analyzer.getFloatTimeDomainData(timeDomainData as Float32Array<ArrayBuffer>);

  return {
    frequencyData: new Float32Array(frequencyData),
    timeDomainData: new Float32Array(timeDomainData),
    rmsAmplitude: calculateRMS(timeDomainData),
    dominantFrequency: getDominantFrequency(frequencyData, audioContext!.sampleRate),
    sampleRate: audioContext!.sampleRate,
    timestamp: performance.now(),
  };
}

export function getWaveformData(): Float32Array | null {
  if (!analyzer || !timeDomainData) return null;
  analyzer.getFloatTimeDomainData(timeDomainData as Float32Array<ArrayBuffer>);
  return timeDomainData;
}

export function getFrequencyData(): Float32Array | null {
  if (!analyzer || !frequencyData) return null;
  analyzer.getFloatFrequencyData(frequencyData as Float32Array<ArrayBuffer>);
  return new Float32Array(frequencyData);
}

function calculateRMS(timeDomain: Float32Array): number {
  let sumSquares = 0;
  for (let i = 0; i < timeDomain.length; i++) {
    sumSquares += timeDomain[i] * timeDomain[i];
  }
  return Math.sqrt(sumSquares / timeDomain.length);
}

function getDominantFrequency(freqData: Float32Array, sampleRate: number): number {
  let maxIndex = 0;
  let maxValue = -Infinity;
  for (let i = 1; i < freqData.length; i++) {
    if (freqData[i] > maxValue) {
      maxValue = freqData[i];
      maxIndex = i;
    }
  }
  return (maxIndex * sampleRate) / (ANALYZER_CONFIG.fftSize);
}

export function getAudioContext(): AudioContext | null {
  return audioContext;
}

let cachedAmplitude = 0;

export function getAudioAmplitude(): number {
  if (!analyzer || !timeDomainData) return 0;
  analyzer.getFloatTimeDomainData(timeDomainData as Float32Array<ArrayBuffer>);
  let sumSquares = 0;
  for (let i = 0; i < timeDomainData.length; i++) {
    sumSquares += timeDomainData[i] * timeDomainData[i];
  }
  cachedAmplitude = Math.sqrt(sumSquares / timeDomainData.length);
  return cachedAmplitude;
}

export function stopAudio(): void {
  stream?.getTracks().forEach((track) => track.stop());
  audioContext?.close();
  audioContext = null;
  analyzer = null;
  stream = null;
}
